import { sendEmailRequest } from '../services/emailDeliveryService';
// src/components/InsightsDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { getPositiveUserId, getStoredUser } from '../utils/authUser';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import './InsightsDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const InsightsDashboard = () => {
  const navigate = useNavigate();
  const currentUser = getStoredUser();
  const currentUserId = getPositiveUserId(currentUser);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyType, setPropertyType] = useState('Residential');
  const [school, setSchool] = useState('');
  const [schools, setSchools] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailNotice, setEmailNotice] = useState(null);
  const [emailSenderScope, setEmailSenderScope] = useState(currentUserId ? 'agent' : 'brokerage');

  useEffect(() => {
    fetchSchools();
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [propertyType, school]); // eslint-disable-line react-hooks/exhaustive-deps -- insights refresh only when filters change

  const fetchSchools = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/get-schools/${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSchools(data || []);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const fetchInsights = async () => {
    try {
      const url = `https://tophamtechbe.com/api/treas/idx/v1/insights/${SITE_CONFIG.SITE_ID}?property_type=${propertyType}${school ? `&school_district=${encodeURIComponent(school)}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights || []);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    setLoadingContacts(true);

    try {
      const response = await fetch(`https://tophamtechbe.com/treas-postgrid/s3/get-address-list/${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to load contacts');
      }

      const data = await response.json();
      setContacts((data.contacts || []).filter(contact => contact.Email?.trim()));
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setEmailNotice({
        type: 'error',
        message: 'Contacts could not be loaded. You can still enter the recipient manually.'
      });
    } finally {
      setLoadingContacts(false);
    }
  };

  const openEmailModal = () => {
    if (!school) {
      setEmailNotice({ type: 'error', message: 'Select a school district before emailing insights.' });
      return;
    }

    setEmailNotice(null);
    setEmailSenderScope(currentUserId ? 'agent' : 'brokerage');
    setShowEmailModal(true);
    if (contacts.length === 0) {
      fetchContacts();
    }
  };

  const closeEmailModal = () => {
    if (sendingEmail) return;
    setShowEmailModal(false);
    setEmailNotice(null);
  };

  const handleContactChange = (event) => {
    const email = event.target.value;
    const contact = contacts.find(item => item.Email === email);

    setRecipientEmail(email);
    setCustomerName(contact?.Name || '');
  };

  const getMarketName = () => {
    const districtName = school.split(' - ')[0].trim();
    if (/school district/i.test(districtName)) return districtName;
    return `${districtName} School District`;
  };

  const getInsightsEmailError = async (response) => {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (error) {}

    switch (errorData.code) {
      case 'AGENT_PROFILE_NOT_FOUND':
        return 'Your personal email profile is missing or inactive. Choose Brokerage-wide or contact support.';
      case 'NO_INSIGHTS_FOUND':
        return 'No market insights were found for the selected filters. Try a different market or property type.';
      case 'INVALID_REQUEST':
        return errorData.message || 'The insights email request is invalid. Please review the recipient and sender selection.';
      case 'INSIGHTS_EMAIL_RATE_LIMITED': {
        const retryAfter = response.headers.get('Retry-After');
        return retryAfter
          ? `Insights email generation is temporarily rate limited. Try again in ${retryAfter} seconds.`
          : 'Insights email generation is temporarily rate limited. Please try again shortly.';
      }
      case 'INSIGHTS_EMAIL_FAILED':
        return 'The insights email could not be generated or delivered. Please try again.';
      default:
        return errorData.message || 'The email service could not send this message.';
    }
  };

  const sendInsightsEmail = async (event) => {
    event.preventDefault();

    if (!customerName.trim() || !recipientEmail.trim()) {
      setEmailNotice({ type: 'error', message: 'Enter the client name and email address.' });
      return;
    }

    if (emailSenderScope === 'agent' && !currentUserId) {
      setEmailNotice({ type: 'error', message: 'Your user account is missing a valid ID. Choose Brokerage-wide or contact support.' });
      return;
    }

    setSendingEmail(true);
    setEmailNotice(null);

    try {
      const requestBody = {
        emailAddress: recipientEmail.trim(),
        customerName: customerName.trim(),
        siteId: SITE_CONFIG.SITE_ID,
        marketName: getMarketName(),
        propertyType,
        schoolDistrict: school
      };
      if (emailSenderScope === 'agent') requestBody.userId = currentUserId;

      const response = await sendEmailRequest('https://tophamtechbe.com/api/treas/comm/send-insights-email', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(await getInsightsEmailError(response));
      }

      const result = await response.json();
      if (result.status !== 'sent') {
        throw new Error('The email service did not confirm delivery. Please try again.');
      }

      const sentTo = result.recipient || recipientEmail.trim();
      const signatureLabel = result.senderScope === 'agent' ? 'your personal signature' : 'the brokerage signature';
      const subjectDetail = result.subject ? ` Subject: “${result.subject}”` : '';
      setShowEmailModal(false);
      setRecipientEmail('');
      setCustomerName('');
      setEmailNotice({ type: 'success', message: `Insights emailed to ${sentTo} using ${signatureLabel}.${subjectDetail}` });
    } catch (error) {
      console.error('Error sending insights email:', error);
      setEmailNotice({ type: 'error', message: error.message || 'Network error sending insights email.' });
    } finally {
      setSendingEmail(false);
    }
  };

  const getMarketTrendsData = () => {
    const areas = [...new Set(insights.map(i => i.area))];
    const dates = [...new Set(insights.map(i => i.date))].sort();
    
    return {
      labels: dates,
      datasets: areas.slice(0, 5).map((area, index) => ({
        label: area,
        data: dates.map(date => {
          const item = insights.find(i => i.area === area && i.date === date && i.property_type === 'Residential');
          return item ? item.median_price : null;
        }),
        borderColor: `hsl(${index * 60}, 70%, 50%)`,
        backgroundColor: `hsla(${index * 60}, 70%, 50%, 0.1)`,
        tension: 0.1
      }))
    };
  };

  const getPriceDistributionData = () => {
    const prices = insights.map(i => i.median_price);
    
    // Adjust price ranges based on property type
    const isLease = propertyType === 'ResidentialLease';
    const ranges = isLease 
      ? ['0-1k', '1k-1.5k', '1.5k-2k', '2k-2.5k', '2.5k+']
      : ['0-100k', '100k-200k', '200k-300k', '300k-400k', '400k+'];
    
    const thresholds = isLease 
      ? [1000, 1500, 2000, 2500]
      : [100000, 200000, 300000, 400000];
    
    return {
      labels: ranges,
      datasets: [{
        label: propertyType,
        data: [
          prices.filter(p => p < thresholds[0]).length,
          prices.filter(p => p >= thresholds[0] && p < thresholds[1]).length,
          prices.filter(p => p >= thresholds[1] && p < thresholds[2]).length,
          prices.filter(p => p >= thresholds[2] && p < thresholds[3]).length,
          prices.filter(p => p >= thresholds[3]).length,
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
      }]
    };
  };

  const getActiveListingsData = () => {
    const areaData = insights.reduce((acc, item) => {
      if (!acc[item.area]) acc[item.area] = 0;
      acc[item.area] += item.active_listings;
      return acc;
    }, {});

    return {
      labels: Object.keys(areaData).slice(0, 10),
      datasets: [{
        label: 'Active Listings',
        data: Object.values(areaData).slice(0, 10),
        backgroundColor: 'rgba(75, 192, 192, 0.8)',
      }]
    };
  };

  const getDaysOnMarketData = () => {
    const daysData = insights
      .filter(i => i.avg_days_on_market !== null)
      .map(i => i.avg_days_on_market);
    
    return {
      labels: ['0-30 days', '31-60 days', '61-90 days', '90+ days'],
      datasets: [{
        label: 'Properties',
        data: [
          daysData.filter(d => d <= 30).length,
          daysData.filter(d => d > 30 && d <= 60).length,
          daysData.filter(d => d > 60 && d <= 90).length,
          daysData.filter(d => d > 90).length,
        ],
        backgroundColor: 'rgba(255, 159, 64, 0.8)',
      }]
    };
  };

  const getLatestInsightsByArea = () => {
    const areaMap = new Map();
    
    // Group by area and keep only the latest date for each area
    insights.forEach(item => {
      const key = `${item.area}-${item.zip_code}`;
      if (!areaMap.has(key) || item.date > areaMap.get(key).date) {
        areaMap.set(key, item);
      }
    });
    
    return Array.from(areaMap.values()).slice(0, 10);
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
    },
    scales: {
      y: { beginAtZero: true }
    }
  };

  if (loading) {
    return <div className="insights-loading">Loading insights...</div>;
  }

  return (
    <div className="insights-dashboard">
      <div className="insights-header">
        <h1>Market Insights Dashboard</h1>
        <div className="insights-controls">
          <select 
            value={propertyType} 
            onChange={(e) => setPropertyType(e.target.value)}
            className="property-type-select"
          >
            <option value="Residential">Residential</option>
            <option value="ResidentialLease">Residential Lease</option>
            <option value="Land">Land</option>
            <option value="Commercial">Commercial</option>
          </select>
          <select 
            value={school} 
            onChange={(e) => {
              setSchool(e.target.value);
              setEmailNotice(null);
            }}
            className="property-type-select"
          >
            <option value="">All Schools</option>
            {schools.map((s, i) => (
              <option key={i} value={s}>{s}</option>
            ))}
          </select>
          <button onClick={openEmailModal} className="email-insights-button">
            Email Insights
          </button>
          <button onClick={() => navigate('/admin')} className="back-admin-button">
            Back to Admin
          </button>
        </div>
      </div>

      {emailNotice && !showEmailModal && (
        <div className={`insights-notice insights-notice-${emailNotice.type}`} role="status">
          {emailNotice.message}
        </div>
      )}

      <div className="charts-grid">
        <div className="chart-container">
          <div className="chart-header">
            <h3>Market Trends</h3>
            <div className="info-wrapper">
              <span className="info-icon" onClick={() => setActiveTooltip(activeTooltip === 'trends' ? null : 'trends')}>
                ⓘ
              </span>
              {activeTooltip === 'trends' && (
                <div className="tooltip-popup">
                  Daily market snapshots showing median price movements across areas. Rising lines = appreciating markets (price aggressively). Falling lines = buyer's market (expect negotiations). Use to identify hot neighborhoods and advise on pricing strategy.
                </div>
              )}
            </div>
          </div>
          <Line data={getMarketTrendsData()} options={chartOptions} />
        </div>

        <div className="chart-container">
          <div className="chart-header">
            <h3>Price Distribution</h3>
            <div className="info-wrapper">
              <span className="info-icon" onClick={() => setActiveTooltip(activeTooltip === 'price' ? null : 'price')}>
                ⓘ
              </span>
              {activeTooltip === 'price' && (
                <div className="tooltip-popup">
                  Distribution of current listings by price range. Shows where inventory is concentrated. Use to identify market gaps, target buyer budgets, and position listings competitively.
                </div>
              )}
            </div>
          </div>
          <Bar data={getPriceDistributionData()} options={chartOptions} />
        </div>

        <div className="chart-container">
          <div className="chart-header">
            <h3>Active Listings by Area</h3>
            <div className="info-wrapper">
              <span className="info-icon" onClick={() => setActiveTooltip(activeTooltip === 'active' ? null : 'active')}>
                ⓘ
              </span>
              {activeTooltip === 'active' && (
                <div className="tooltip-popup">
                  Current active listings by area - your competition. Higher bars = more inventory (buyer's market). Lower bars = limited supply (seller's market). Use to assess market saturation and opportunity.
                </div>
              )}
            </div>
          </div>
          <Bar data={getActiveListingsData()} options={chartOptions} />
        </div>

        <div className="chart-container">
          <div className="chart-header">
            <h3>Days on Market Distribution</h3>
            <div className="info-wrapper">
              <span className="info-icon" onClick={() => setActiveTooltip(activeTooltip === 'days' ? null : 'days')}>
                ⓘ
              </span>
              {activeTooltip === 'days' && (
                <div className="tooltip-popup">
                  Market velocity indicator. 0-30 days = hot market (expect multiple offers). 90+ days = slow market (need creative marketing). Use to set realistic timeline expectations and pricing strategy.
                </div>
              )}
            </div>
          </div>
          <Bar data={getDaysOnMarketData()} options={chartOptions} />
        </div>

        <div className="chart-container full-width">
          <div className="chart-header">
            <h3>Top Performing Areas</h3>
            <div className="info-wrapper">
              <span className="info-icon" onClick={() => setActiveTooltip(activeTooltip === 'table' ? null : 'table')}>
                ⓘ
              </span>
              {activeTooltip === 'table' && (
                <div className="tooltip-popup">
                  Latest market snapshot by area. Price Trend compares today's median price vs. previous day. Use to identify emerging opportunities and advise clients on micro-market conditions.
                </div>
              )}
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Zip Code</th>
                  <th>Median Price</th>
                  <th>Active Listings</th>
                  <th>Price Trend</th>
                </tr>
              </thead>
              <tbody>
                {getLatestInsightsByArea().map((item, index) => (
                  <tr key={index}>
                    <td>{item.area}</td>
                    <td>{item.zip_code}</td>
                    <td>${item.median_price?.toLocaleString()}</td>
                    <td>{item.active_listings}</td>
                    <td className={`trend ${item.price_trend_vs_last_month?.toLowerCase()}`}>
                      {item.price_trend_vs_last_month}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showEmailModal && (
        <div className="insights-modal-overlay" onClick={closeEmailModal}>
          <div
            className="insights-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-insights-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="insights-modal-header">
              <div>
                <h2 id="email-insights-title">Email Market Insights</h2>
                <p>{getMarketName()} · {propertyType}</p>
              </div>
              <button
                type="button"
                className="insights-modal-close"
                onClick={closeEmailModal}
                aria-label="Close"
                disabled={sendingEmail}
              >
                ×
              </button>
            </div>

            <form onSubmit={sendInsightsEmail} className="insights-email-form">
              <fieldset className="insights-sender-scope" disabled={sendingEmail}>
                <legend>Signature</legend>
                <label className={`insights-scope-option ${emailSenderScope === 'agent' ? 'selected' : ''} ${!currentUserId ? 'disabled' : ''}`}>
                  <input
                    type="radio"
                    name="insights-sender-scope"
                    checked={emailSenderScope === 'agent'}
                    disabled={!currentUserId || sendingEmail}
                    onChange={() => {
                      setEmailSenderScope('agent');
                      setEmailNotice(null);
                    }}
                  />
                  <span><strong>Personal</strong><small>Use your agent profile and signature.</small></span>
                </label>
                <label className={`insights-scope-option ${emailSenderScope === 'brokerage' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="insights-sender-scope"
                    checked={emailSenderScope === 'brokerage'}
                    disabled={sendingEmail}
                    onChange={() => {
                      setEmailSenderScope('brokerage');
                      setEmailNotice(null);
                    }}
                  />
                  <span><strong>Brokerage-wide</strong><small>Use the brokerage signature.</small></span>
                </label>
              </fieldset>

              <label htmlFor="insights-contact">Select an existing client</label>
              <select
                id="insights-contact"
                value={contacts.some(contact => contact.Email === recipientEmail) ? recipientEmail : ''}
                onChange={handleContactChange}
                disabled={loadingContacts || sendingEmail}
              >
                <option value="">{loadingContacts ? 'Loading clients...' : 'Enter manually'}</option>
                {contacts.map((contact, index) => (
                  <option key={`${contact.Email}-${index}`} value={contact.Email}>
                    {contact.Name || contact.Email} ({contact.Email})
                  </option>
                ))}
              </select>

              <label htmlFor="insights-customer-name">Client name</label>
              <input
                id="insights-customer-name"
                type="text"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                autoComplete="name"
                required
                disabled={sendingEmail}
              />

              <label htmlFor="insights-recipient-email">Client email</label>
              <input
                id="insights-recipient-email"
                type="email"
                value={recipientEmail}
                onChange={(event) => setRecipientEmail(event.target.value)}
                autoComplete="email"
                required
                disabled={sendingEmail}
              />

              {emailNotice && (
                <div className={`insights-form-notice insights-form-notice-${emailNotice.type}`} role="alert">
                  {emailNotice.message}
                </div>
              )}

              <div className="insights-modal-actions">
                <button type="button" className="insights-cancel-button" onClick={closeEmailModal} disabled={sendingEmail}>
                  Cancel
                </button>
                <button type="submit" className="email-insights-button" disabled={sendingEmail}>
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsDashboard;
